import { CENTRAL_API_BASE_URL } from '../../constants/central-api';

const SESSION_FROM_SELLER_API = CENTRAL_API_BASE_URL + 'session-from-seller';

/**
 * Re-mint the agency session from a SELLER JWT — the admin "login as seller"
 * handoff that lands on `/login-via-portal?token=<jwt>`.
 *
 * The super-admin panel (admin.adup.io) impersonates a seller and hands the
 * portal ONLY the legacy seller JWT — no agency `emp_` key and no `tara_session`
 * cookie (the panel calls central-api server-side, so any Set-Cookie lands on
 * the panel, not the browser). The tara26 portal is employee-centric, so without
 * the agency key every `/api/v1/me` + `/api/v1/agency/*` call 401s `invalid_token`
 * and the portal is dark.
 *
 * This sends the seller JWT to `GET /api/v1/session-from-seller`; the backend
 * mints the agency key (the SAME bridge a normal login uses) and returns the
 * login payload (`{ access_token, user, agency_api_key }`), which the caller
 * applies via `applySessionFromResponse`. `credentials: 'include'` lets the
 * backend also set the `tara_session` cookie (this XHR is first-party to the
 * portal origin), so a later reload restores via `GET /session`.
 *
 * Returns the `data` object on success, or `null` on any failure (never throws)
 * — the caller falls back to the seller-only legacy view.
 */
export async function restoreSessionFromSeller(
  sellerToken: string
): Promise<any | null> {
  try {
    const response = await fetch(SESSION_FROM_SELLER_API, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${sellerToken}`,
      },
      credentials: 'include',
    });

    if (!response.ok) {
      return null;
    }

    const json = await response.json();

    // ApiResponse envelope: { success, message, data: { access_token, user,
    // agency_api_key } }. Require the agency key — that is the whole point.
    if (json?.success === true && json?.data?.agency_api_key) {
      return json.data;
    }

    return null;
  } catch {
    return null;
  }
}
